import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const HowToUse = ({ features }) => {
    const [activeTab, setActiveTab] = useState('start');

    const hasPaid = (key) => features?.[key];

    const tabs = [
        { id: 'start',   icon: 'fa-rocket',       label: 'Getting Started' },
        { id: 'menu',    icon: 'fa-utensils',      label: 'Menu & Categories' },
        { id: 'qr',      icon: 'fa-qrcode',        label: 'QR Code' },
        { id: 'pos',     icon: 'fa-cash-register', label: 'POS Billing' },
        { id: 'kitchen', icon: 'fa-fire-burner',   label: 'Kitchen (KDS)' },
        { id: 'service-requests', icon: 'fa-bell-concierge', label: 'Service Requests', paid: !hasPaid('has_staff_management') },
        { id: 'staff',   icon: 'fa-users-cog',     label: 'Staff Management', paid: !hasPaid('has_staff_management') },
        { id: 'analytics',icon: 'fa-chart-line',   label: 'Analytics' },
        { id: 'offers',  icon: 'fa-tags',          label: 'Offers',          paid: !hasPaid('has_offer_popup') },
        { id: 'coupons', icon: 'fa-ticket-alt',    label: 'Coupons',         paid: !hasPaid('has_coupon_system') },
        { id: 'notif',   icon: 'fa-bell',          label: 'Notifications',   paid: !hasPaid('has_push_notification') },
        { id: 'loyalty', icon: 'fa-gift',          label: 'Loyalty Program', paid: !hasPaid('has_loyalty_system') },
        { id: 'gallery', icon: 'fa-images',        label: 'Gallery' },
        { id: 'monetization', icon: 'fa-dollar-sign', label: 'Monetization', paid: !hasPaid('has_ad_monetization') },
        { id: 'announcements', icon: 'fa-bullhorn', label: 'Announcements' },
        { id: 'settings',icon: 'fa-cogs',          label: 'Settings' },
        { id: 'billing', icon: 'fa-file-invoice-dollar', label: 'Billing Config' },
        { id: 'subscription', icon: 'fa-receipt',  label: 'Subscription' },
        { id: 'bulk',    icon: 'fa-bolt',          label: 'Bulk Entry' },
        { id: 'faq',     icon: 'fa-question-circle', label: 'FAQ' },
    ];

    // ── Shared Step component ──────────────────────────────────────────────
    const Step = ({ num, title, children }) => (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '28px' }}>
            <div style={{ flexShrink: 0, width: '36px', height: '36px', borderRadius: '50%', background: 'var(--primary-orange, #ea580c)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '1em' }}>
                {num}
            </div>
            <div>
                <h3 style={{ margin: '4px 0 8px 0', fontSize: '1.05em', color: 'var(--text-color)' }}>{title}</h3>
                <div style={{ color: 'var(--text-color-secondary)', lineHeight: '1.7', fontSize: '0.95em' }}>{children}</div>
            </div>
        </div>
    );

    const Tip = ({ children }) => (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 16px', margin: '16px 0', fontSize: '0.9em', color: '#92400e' }}>
            <i className="fas fa-lightbulb" style={{ marginRight: '8px', color: '#f59e0b' }} />
            <strong>Tip:</strong> {children}
        </div>
    );

    const NavLink = ({ to, label }) => (
        <Link to={`/manage_v2_secure_auth/${to}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '8px', padding: '7px 16px', background: 'var(--primary-orange, #ea580c)', color: '#fff', borderRadius: '50px', fontSize: '0.88em', fontWeight: '600', textDecoration: 'none' }}>
            <i className="fas fa-arrow-right" /> {label}
        </Link>
    );

    const PaidBadge = () => (
        <span style={{ marginLeft: '8px', fontSize: '0.72em', background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: '50px', fontWeight: '700', verticalAlign: 'middle' }}>
            PAID
        </span>
    );

    const LockedNotice = ({ feature }) => (
        <div style={{ background: '#f9fafb', border: '2px dashed #d1d5db', borderRadius: '10px', padding: '24px', textAlign: 'center', color: '#6b7280' }}>
            <i className="fas fa-lock" style={{ fontSize: '2rem', marginBottom: '12px', display: 'block', color: '#9ca3af' }} />
            <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: 'var(--text-color)' }}>
                {feature} is a Paid Plan Feature
            </p>
            <p style={{ margin: '0 0 16px 0', fontSize: '0.9em' }}>
                Upgrade your subscription to unlock this feature and view its guide.
            </p>
            <Link to="/manage_v2_secure_auth/subscription" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 20px', background: 'var(--primary-orange, #ea580c)', color: '#fff', borderRadius: '50px', fontSize: '0.9em', fontWeight: '600', textDecoration: 'none' }}>
                <i className="fas fa-crown" /> Upgrade Now
            </Link>
        </div>
    );

    // ── Tab Content Map ────────────────────────────────────────────────────
    const content = {
        start: (
            <div>
                <p style={{ color: 'var(--text-color-secondary)', marginBottom: '28px', fontSize: '0.95em', lineHeight: '1.7' }}>
                    Follow these 3 steps to launch your digital menu in under 10 minutes.
                </p>
                <Step num={1} title="Create Your Food Categories">
                    Before adding items, decide how you want to group them — e.g. <em>Starters</em>, <em>Main Course</em>, <em>Drinks</em>, <em>Desserts</em>.
                    <br /><br />
                    Go to <strong>Manage Categories</strong> and create each one. They will appear as section headings on your customer menu.
                    <NavLink to="manage-categories" label="Manage Categories" />
                </Step>
                <Step num={2} title="Add Your Menu Items">
                    Once categories exist, go to <strong>Manage My Menu</strong> to add each dish. Fill in the name, price, category, and optionally a photo.
                    <br /><br />
                    <strong>Pro tip:</strong> Use <strong>Rapid Bulk Entry</strong> if you have many items — paste them all at once instead of one by one.
                    <NavLink to="manage-menu" label="Add Menu Items" />
                </Step>
                <Step num={3} title="Download & Print Your QR Code">
                    Once your menu is ready, go to <strong>QR Templates</strong> to download a printable QR code. Place it on your tables — customers scan it to view the menu instantly.
                    <NavLink to="qr-templates" label="Get QR Code" />
                </Step>
                <Tip>Complete your Restaurant Settings (logo, contact, theme colour) to make your menu look professional. A complete profile builds customer trust.</Tip>
            </div>
        ),
        menu: (
            <div>
                <Step num={1} title="Create Categories First">
                    Categories organise your menu. Go to <strong>Manage Categories</strong>, click <strong>Add New Category</strong>, type a name and save.
                    You can drag to reorder them later.
                    <NavLink to="manage-categories" label="Manage Categories" />
                </Step>
                <Step num={2} title="Add Items to a Category">
                    In <strong>Manage My Menu</strong>, click <strong>Add New Item</strong>. Fill in:
                    <ul style={{ marginTop: '8px', paddingLeft: '20px', lineHeight: '1.9' }}>
                        <li><strong>Name</strong> — display name on the menu</li>
                        <li><strong>Category</strong> — choose from your created categories</li>
                        <li><strong>Type</strong> — Veg 🟢, Non-Veg 🔴, or Add-on ⚪</li>
                        <li><strong>Price</strong> — single price, or multiple sizes (e.g. Half / Full)</li>
                        <li><strong>Description</strong> — optional short description</li>
                        <li><strong>Image</strong> — optional photo of the dish</li>
                    </ul>
                    <NavLink to="manage-menu" label="Add Items" />
                </Step>
                <Step num={3} title="Mark Popular & Recommended Items">
                    When editing an item you can tick <strong>Mark as Popular</strong> or <strong>Mark as Recommended</strong>.
                    These items get a special badge on the customer menu — great for upselling.
                </Step>
                <Step num={4} title="Edit or Delete Items">
                    Click the pencil ✏️ icon next to any item to edit it, or the trash 🗑️ icon to delete. Changes are live immediately.
                </Step>
                <Tip>Keep item names short and clear. Customers read menus quickly — a simple "Paneer Tikka" is better than "Fresh Homemade Paneer Tikka Special".</Tip>
            </div>
        ),
        qr: (
            <div>
                <Step num={1} title="Go to QR Templates">
                    Navigate to <strong>Download QR Templates</strong> from the dashboard.
                    <NavLink to="qr-templates" label="QR Templates" />
                </Step>
                <Step num={2} title="Choose a Template Style">
                    Pick a template that matches your restaurant's look. Each template already has your QR code embedded.
                </Step>
                <Step num={3} title="Download & Print">
                    Click <strong>Download</strong>. Print the image on A4 paper or get it laminated. Place one on every table.
                </Step>
                <Step num={4} title="Test the QR Code">
                    Open your phone camera, point at the QR code — it should open your live menu instantly. No app needed for customers.
                </Step>
                <Tip>Add your restaurant name and social handles on a custom sticker around the QR code to make it look premium.</Tip>
            </div>
        ),
        pos: (
            <div>
                <Step num={1} title="Launch the POS Billing System">
                    Click on <strong>POS Billing</strong> to open the full checkout interface. This is meant for your front-desk staff to take walk-in or phone orders.
                </Step>
                <Step num={2} title="Process an Order">
                    Select items from the category menu to add them to the cart. You can increase quantities or remove items instantly. The system will automatically calculate the subtotal.
                </Step>
                <Step num={3} title="Apply Discounts & Settle">
                    Expand the <strong>Discounts & Taxes</strong> panel to apply a manual discount or a pre-defined coupon. Then, enter the customer's phone number and select the payment mode (Cash, Card, UPI) to <strong>Settle Payment</strong> and print the receipt.
                </Step>
            </div>
        ),
        analytics: (
            <div>
                <Step num={1} title="View Your Analytics Dashboard">
                    Go to <strong>Analytics</strong> to view your menu's performance over time. You can filter the data by <em>Last 7 Days</em>, <em>Last 30 Days</em>, or an <em>All Time</em> view.
                </Step>
                <Step num={2} title="Track Traffic">
                    The Traffic chart shows you how many times your QR codes were scanned and your menu viewed.
                </Step>
                <Step num={3} title="Monitor Sales & Payments">
                    If you use the built-in POS Billing system, your completed orders and total revenue will automatically sync here, including a breakdown of payment methods used.
                </Step>
            </div>
        ),
        settings: (
            <div>
                <Step num={1} title="Restaurant Settings">
                    Go to <strong>Settings</strong> to update your core details. Upload your logo (appears on the customer menu and invoices), set your business phone number, and choose your brand's Theme Color.
                </Step>
            </div>
        ),
        billing: (
            <div>
                <Step num={1} title="Configure Taxes">
                    Go to <strong>Billing Config</strong> to configure the tax rates you charge on your food (like CGST/SGST). These will automatically apply during POS checkout.
                </Step>
                <Step num={2} title="Invoice Setup">
                    Make sure your full Restaurant Address is entered here. This address, along with your phone number and logo, will be printed on the headers of all POS generated invoices.
                </Step>
            </div>
        ),
        offers: !hasPaid('has_offer_popup') ? <LockedNotice feature="Special Offers" /> : (
            <div>
                <Step num={1} title="What is the Offer Popup?">
                    When a customer opens your menu, a promotional popup appears showing your offer images. This is great for daily specials, combo offers, or seasonal promotions.
                </Step>
                <Step num={2} title="Upload an Offer Image">
                    Go to <strong>Manage Special Offers</strong>. You have <strong>3 slots</strong>. Upload a square (1:1) image for each active offer.
                    <NavLink to="manage-offers" label="Manage Offers" />
                </Step>
                <Step num={3} title="Best Practices">
                    <ul style={{ paddingLeft: '20px', lineHeight: '1.9' }}>
                        <li>Use bold text on the image (e.g. "Buy 2 Get 1 Free")</li>
                        <li>Keep the offer image clean and readable on mobile</li>
                        <li>Change offers weekly to keep the menu fresh</li>
                    </ul>
                </Step>
                <Tip>Use free tools like Canva to design professional offer images in minutes.</Tip>
            </div>
        ),
        coupons: !hasPaid('has_coupon_system') ? <LockedNotice feature="Coupon System" /> : (
            <div>
                <Step num={1} title="What are Coupons?">
                    Coupons let you offer percentage or flat discounts to customers who enter a code. You control the code, discount amount, and validity period.
                </Step>
                <Step num={2} title="Create a Coupon">
                    Go to <strong>Manage Coupons</strong>, click <strong>Add New Coupon</strong> and fill in:
                    <ul style={{ paddingLeft: '20px', lineHeight: '1.9', marginTop: '8px' }}>
                        <li><strong>Code</strong> — the code customers enter (e.g. <code>WELCOME20</code>)</li>
                        <li><strong>Discount type</strong> — percentage or flat amount</li>
                        <li><strong>Expiry date</strong> — when the coupon expires</li>
                        <li><strong>Usage limit</strong> — max number of times it can be used</li>
                    </ul>
                    <NavLink to="manage-coupons" label="Manage Coupons" />
                </Step>
                <Step num={3} title="Share the Code">
                    Share the coupon code on WhatsApp, Instagram, or print it on flyers. Customers enter it at checkout.
                </Step>
            </div>
        ),
        notif: !hasPaid('has_push_notification') ? <LockedNotice feature="Push Notifications" /> : (
            <div>
                <Step num={1} title="What are Push Notifications?">
                    You can send messages directly to customers who have scanned your QR code and allowed notifications — like a free SMS broadcast, right to their phone.
                </Step>
                <Step num={2} title="View Your Subscribers">
                    Go to <strong>Send Notifications</strong> to see how many customers are subscribed. The count is also visible on your main dashboard.
                    <NavLink to="manage-notifications" label="Send Notifications" />
                </Step>
                <Step num={3} title="Send a Notification">
                    Write a short message (e.g. "🎉 Happy Hours: 20% off all drinks today 5–8 PM!") and click Send. All subscribed customers receive it instantly.
                </Step>
                <Tip>Keep notifications short and time-sensitive. Sending 1–2 per week is ideal — too many will cause customers to unsubscribe.</Tip>
            </div>
        ),
        kitchen: (
            <div>
                <Step num={1} title="What is the Kitchen Display System?">
                    The KDS (Kitchen Display System) allows your chefs to view active orders in real-time on a tablet or screen in the kitchen, completely replacing paper KOTs (Kitchen Order Tickets).
                </Step>
                <Step num={2} title="Managing Orders">
                    As soon as an order is placed on the POS, it appears on the KDS. The chef can mark the order as "Preparing" and then "Served" when it's ready.
                    <NavLink to="kitchen" label="View Kitchen Display" />
                </Step>
            </div>
        ),
        'service-requests': !hasPaid('has_staff_management') ? <LockedNotice feature="Service Requests" /> : (
            <div>
                <Step num={1} title="What are Service Requests?">
                    Customers can ping your staff directly from their digital menu to request a Waiter, Water, or the Bill. This minimizes wait times and improves the dining experience.
                </Step>
                <Step num={2} title="Monitor Requests">
                    Go to <strong>Service Requests</strong> to see a real-time list of all active table pings. You will hear an audible "beep" when a new request comes in.
                    <NavLink to="service-requests" label="Manage Service Requests" />
                </Step>
                <Step num={3} title="Resolve Requests">
                    Once a staff member attends to the table, click <strong>Mark as Resolved</strong> to clear the request from the screen. Waiters can also view and resolve these requests directly from their Waiter Dashboard.
                </Step>
            </div>
        ),
        staff: !hasPaid('has_staff_management') ? <LockedNotice feature="Staff Management" /> : (
            <div>
                <Step num={1} title="Manage Your Team">
                    Staff Management lets you add Waiters and Kitchen Staff with their own limited access logins. They won't see your analytics or settings.
                </Step>
                <Step num={2} title="Add Staff Members">
                    Click <strong>Add Staff</strong> to create a login ID and password for a team member and assign them a role.
                    <NavLink to="staff" label="Manage Staff" />
                </Step>
            </div>
        ),
        gallery: (
            <div>
                <Step num={1} title="Showcase Your Restaurant">
                    The Gallery allows you to upload photos of your ambiance, events, or signature dishes to display on your customer-facing digital menu.
                </Step>
                <Step num={2} title="Upload Images">
                    Upload high-quality images. They will appear in a swipeable carousel at the bottom of your digital menu.
                    <NavLink to="gallery" label="Manage Gallery" />
                </Step>
            </div>
        ),
        loyalty: !hasPaid('has_loyalty_system') ? <LockedNotice feature="Loyalty Program" /> : (
            <div>
                <Step num={1} title="Reward Your Customers">
                    The Loyalty Program automatically rewards customers with points for every order they place. They can redeem these points for discounts on future visits.
                </Step>
                <Step num={2} title="Configure Points">
                    Set how many points are earned per ₹100 spent, and how much each point is worth when redeeming.
                    <NavLink to="loyalty" label="Manage Loyalty Program" />
                </Step>
            </div>
        ),
        monetization: !hasPaid('has_ad_monetization') ? <LockedNotice feature="Ad Monetization" /> : (
            <div>
                <Step num={1} title="Earn Extra Revenue">
                    Ad Monetization allows you to display non-intrusive advertisements on your digital menu, earning you money based on views and clicks.
                </Step>
                <Step num={2} title="Enable Ads">
                    Opt-in to the monetization program to start displaying ads. You can track your estimated earnings and payouts directly from this dashboard.
                    <NavLink to="monetization" label="Ad Monetization" />
                </Step>
            </div>
        ),
        announcements: (
            <div>
                <Step num={1} title="System Updates">
                    The Announcements page shows important updates, new features, and maintenance notices from the Smart Digital Menu team.
                </Step>
                <Step num={2} title="Stay Informed">
                    Check this page regularly to learn about new tools that can help grow your business.
                    <NavLink to="announcements" label="View Announcements" />
                </Step>
            </div>
        ),
        subscription: (
            <div>
                <Step num={1} title="Manage Your Plan">
                    View your current active plan, its expiry date, and the limits on your account (like maximum orders per month).
                </Step>
                <Step num={2} title="Upgrade or Renew">
                    Easily upgrade to a higher tier or renew your current subscription before it expires to avoid any service interruptions.
                    <NavLink to="subscription" label="Manage Subscription" />
                </Step>
            </div>
        ),
        bulk: (
            <div>
                <Step num={1} title="When to Use Bulk Entry">
                    If you have 10+ items to add, Bulk Entry saves you time. Instead of adding one item at a time, you paste all items at once or import an Excel/CSV file.
                    <NavLink to="bulk-entry" label="Rapid Bulk Entry" />
                </Step>
                <Step num={2} title="Method 1: Paste Text">
                    Go to <strong>Bulk Entry → Paste Text tab</strong>. Type or paste one item per line in the format:
                    <div style={{ fontFamily: 'monospace', background: 'var(--bg-color)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px 16px', margin: '12px 0', fontSize: '0.9em', lineHeight: '1.8' }}>
                        Paneer Butter Masala - 220<br />
                        [Biryani] Chicken Biryani - 299<br />
                        Dal Makhani - 180 | Slow-cooked lentils
                    </div>
                    Use <kbd style={{ background: '#e5e7eb', padding: '1px 6px', borderRadius: '4px', border: '1px solid #d1d5db' }}>Ctrl</kbd> + <kbd style={{ background: '#e5e7eb', padding: '1px 6px', borderRadius: '4px', border: '1px solid #d1d5db' }}>Enter</kbd> to parse instantly.
                </Step>
                <Step num={3} title="Method 2: Import from Excel/CSV">
                    Click <strong>Download Template</strong> and open the file in Excel or Google Sheets. Fill in your items (supports single price or up to 3 size variants), save as CSV, then upload it back.
                </Step>
                <Step num={4} title="Review and Upload">
                    After parsing, review the preview table. You can edit, duplicate, or delete rows before uploading. When ready, click <strong>Upload Items to Menu</strong>.
                </Step>
            </div>
        ),
        faq: (
            <div>
                {[
                    {
                        q: 'Can customers order through the menu?',
                        a: 'The digital menu is a view-only catalogue — it helps customers browse your offerings easily on their phone. Order-taking is handled by your staff as usual.'
                    },
                    {
                        q: 'How do I change my menu theme / colours?',
                        a: 'Go to Restaurant Settings → scroll to Theme Color. Changes are applied live to your customer-facing menu immediately.'
                    },
                    {
                        q: 'How do I update my restaurant logo?',
                        a: 'Go to Restaurant Settings → Logo section. Upload a square PNG or JPG image (recommended: 512×512 px).'
                    },
                    {
                        q: 'Why is my QR code not working after I changed a setting?',
                        a: 'The QR code points to a permanent URL — it should always work. Try clearing your browser cache or scanning from a different phone to confirm.'
                    },
                    {
                        q: 'Can I have items with multiple price options (e.g. Half and Full)?',
                        a: 'Yes. When adding or editing an item in Manage My Menu, click "Add Size" to create multiple size-price combinations.'
                    },
                    {
                        q: 'My subscription expired. What happens to my menu?',
                        a: 'Your menu will remain visible to customers during the grace period, but you won\'t be able to edit it. Renew from the Subscription page to regain full access.'
                    },
                    {
                        q: 'How do I contact support?',
                        a: 'Reach out via the contact details on the Subscription page, or through the WhatsApp link provided during onboarding.'
                    },
                ].map(({ q, a }, i) => (
                    <div key={i} style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)', paddingBottom: '16px', marginBottom: '16px' }}>
                        <p style={{ margin: '0 0 6px 0', fontWeight: '700', color: 'var(--text-color)', fontSize: '0.97em' }}>
                            <i className="fas fa-circle-question" style={{ marginRight: '8px', color: 'var(--primary-orange)' }} />{q}
                        </p>
                        <p style={{ margin: 0, color: 'var(--text-color-secondary)', lineHeight: '1.65', fontSize: '0.93em', paddingLeft: '24px' }}>{a}</p>
                    </div>
                ))}
            </div>
        ),
    };

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
            <h1 style={{ marginTop: 0, marginBottom: '6px' }}>How to Use Your Dashboard</h1>
            <p style={{ color: 'var(--text-color-secondary)', marginBottom: '28px', fontSize: '0.95em' }}>
                Everything you need to know to manage your digital menu like a pro.
            </p>

            <div style={{ display: 'flex', gap: '0', flexWrap: 'wrap', borderBottom: '2px solid var(--border-color, #e5e7eb)', marginBottom: '32px' }}>
                {tabs.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setActiveTab(t.id)}
                        style={{
                            padding: '10px 16px',
                            border: 'none',
                            borderBottom: activeTab === t.id ? '2px solid var(--primary-orange, #ea580c)' : '2px solid transparent',
                            marginBottom: '-2px',
                            background: 'none',
                            cursor: 'pointer',
                            fontWeight: activeTab === t.id ? '700' : '500',
                            color: activeTab === t.id ? 'var(--primary-orange, #ea580c)' : 'var(--text-color-secondary, #6b7280)',
                            fontSize: '0.9em',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            transition: 'color 0.2s',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <i className={`fas ${t.icon}`} />
                        {t.label}
                        {t.paid && <PaidBadge />}
                    </button>
                ))}
            </div>

            <div style={{ maxWidth: '700px' }}>
                {content[activeTab]}
            </div>
        </div>
    );
};

export default HowToUse;
